'use client';

import type { ClassValue } from 'clsx';

import {
  ProductVariantSwatch,
  ProductVariantSwatchImage,
  ProductVariantSwatchLink,
  ProductVariantSwatchMedia,
  ProductVariantSwatchVariant,
  ProductVariantSwatches,
  ProductVariantSwatchesLabel,
  ProductVariantSwatchesList,
  ProductVariantSwatchesTerm,
  ProductVariantSwatchesValue,
} from '@vs-shop/commerce-ui/productVariantSwatches';
import { ImageMissingPlaceholder } from '@vs-shop/ui/imageMissingPlaceholder';
import { clsxm } from '@vs-shop/utils';
import { useState } from 'react';

/**
 * rcprofi's arrangement of the shared variant swatch row, and the one piece of
 * it that is state: which finish a pointer is over.
 *
 * The label names the finish in view until a pointer crosses a swatch, and then
 * names that one instead. Holding it here is what lets every shared part stay a
 * plain component — the label's value is a child and the swatches' handlers ride
 * the native prop spread.
 *
 * Everything it renders is already resolved: the labels are diffed against
 * sibling variant names, the image URLs are through the media pipeline and the
 * copy is formatted, all in `ProductPanelColors` on the server.
 */

/** One finish, as the row needs it: nothing here is a catalogue value. */
export type ProductColorSwatch = {
  readonly code: string;
  /** The variant's full name — what the link is announced as. */
  readonly name: string;
  readonly href: string;
  /** What this finish is CALLED, diffed down from the name. */
  readonly label: string;
  /** Final and renderable, or absent where the finish has no photograph yet. */
  readonly imageSrc: string | null;
  readonly isCurrent: boolean;
};

type Props = {
  /** What the line is about — "Barva:". */
  term: string;
  /** The finish named while no pointer is over the row. */
  defaultLabel: string;
  swatches: readonly ProductColorSwatch[];
  className?: ClassValue;
};

export default function ProductPanelColorsRow({ term, defaultLabel, swatches, className }: Props) {
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);

  return (
    <ProductVariantSwatches className={clsxm(className)}>
      <ProductVariantSwatchesLabel>
        <ProductVariantSwatchesTerm>{term}</ProductVariantSwatchesTerm>
        <ProductVariantSwatchesValue>{hoveredLabel ?? defaultLabel}</ProductVariantSwatchesValue>
      </ProductVariantSwatchesLabel>

      <ProductVariantSwatchesList>
        {swatches.map((swatch) => (
          <ProductVariantSwatch
            key={swatch.code}
            variant={
              swatch.isCurrent
                ? ProductVariantSwatchVariant.CURRENT
                : ProductVariantSwatchVariant.PLAIN
            }
            onMouseEnter={() => {
              setHoveredLabel(swatch.label);
            }}
            onMouseLeave={() => {
              setHoveredLabel(null);
            }}
          >
            {/* The thumbnail is the whole target and carries no text, so the
                link is named explicitly rather than left to the picture. */}
            <ProductVariantSwatchLink href={swatch.href} aria-label={swatch.name}>
              <ProductVariantSwatchMedia>
                {swatch.imageSrc === null ? (
                  <ImageMissingPlaceholder />
                ) : (
                  <ProductVariantSwatchImage
                    src={swatch.imageSrc}
                    alt={swatch.name}
                    width={101}
                    height={76}
                  />
                )}
              </ProductVariantSwatchMedia>
            </ProductVariantSwatchLink>
          </ProductVariantSwatch>
        ))}
      </ProductVariantSwatchesList>
    </ProductVariantSwatches>
  );
}
